import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { makeStyles, Hidden, CircularProgress } from '@material-ui/core';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import { translate } from 'components/Translation';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import BundleLeft from 'views/Numbers/Details/Usage/common/BundleLeft';
import NoBundle from 'views/Numbers/Details/Usage/common/NoBundle';
import Icons from 'views/Numbers/Details/Usage/common/Icons';
import { Package, Total } from 'utils/api/models/numbers/usage';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/usageStyle';
import { HeadingLBold, Caption, BodyS } from 'components/Typography/Typography';
import SinglePackage from './subcomponents/SinglePackage';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { NumbersService } from 'services/numbers/numbers';
import { DataUsage } from 'utils/api/models/numbers/dataUsage';
import CustomLinearProgress from 'components/CustomLinearProgress/CustomLinearProgress';
import { UsageLogosService } from 'services/usageLogos/usageLogos';
import { SingleUsageLogo } from 'utils/api/models/usageLogos/singleUsageLogo';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IDetailsProps {
	match: {
		params: {
			type: string;
		};
		url: string;
	};
	bundles: {
		totals: Total[];
		packages: Package[];
	};
	phoneNumber: string;
}

const SingleUsageDetails = ({ match, bundles, phoneNumber }: IDetailsProps) => {
	const classes = useStyles();
	const { type } = match.params;
	const [dataUsages, setDataUsages] = useState<DataUsage[]>([]);
	const [dataUsageLogos, setDataUsageLogos] = useState<SingleUsageLogo[]>([]);
	const [dataLoading, setDataLoading] = useState(false);

	useEffect(() => {
		if (type === 'data') {
			setDataLoading(true);
			NumbersService.getDataUsage(phoneNumber).subscribe(
				(dataUsages) => {
					setDataLoading(false);
					setDataUsages(dataUsages.dataUsages || []);
				},
				() => {
					setDataLoading(false);
				}
			);
			UsageLogosService.getUsageLogos().subscribe((resp) => {
				setDataUsageLogos(resp.usageLogos);
			});
		}
	}, [type]);

	const Icon = (group: string) => {
		return <img src={Icons(type, group)} alt="" />;
	};

	const renderPackages = (packages: Package[], icon: React.ReactElement) => {
		if (packages.length === 0) {
			return <NoBundle />;
		}

		return (
			<GridContainer justify="flex-start">
				{packages.map((singlePackage, index: number) => {
					return (
						<GridItem xs={12} md={6} lg={4} className={classes.specialPackage} key={index}>
							<div className={classes.specialPackageIcon}>{icon}</div>
							<Card className={classes.specialPackageCard}>
								<div className={classes.thing}>&nbsp;</div>
								<CardBody className={classes.bundleCardBody}>
									<SinglePackage singlePackage={singlePackage} />
								</CardBody>
							</Card>
						</GridItem>
					);
				})}
			</GridContainer>
		);
	};

	// No packages - no page
	if (bundles.totals.length === 0) {
		return <Redirect to="/dashboard/error-page" />;
	}

	const getCorrectIcon = (name: string) => {
		const mappedLogo = dataUsageLogos.find((logo) => logo.usageTextId === name);
		if (mappedLogo) {
			return UsageLogosService.getImageByPartialPath(mappedLogo.usageLogo);
		} else {
			return '';
		}
	};

	function getBackgroundColor(name: string) {
		let stringUniqueHash = [...name].reduce((acc, char) => {
			return char.charCodeAt(0) + ((acc << 5) - acc);
		}, 0);
		return `hsl(${stringUniqueHash % 360}, 50%, 70%)`;
	}

	const defineTotalUsage = (data: Total) => {
		let totalUsage = 0;

		if (data.totalPackageSize.value === 0) {
			dataUsages.forEach((du) => {
				totalUsage += data.totalBalance.unit === 'MB' ? du.usageMb : du.usageGB;
			});
		} else {
			totalUsage = data.totalPackageSize.value;
		}
		return totalUsage;
	};

	const DataUsages = ({ data }: { data: Total }) => {
		if (dataUsages && dataUsages.length === 0) {
			if (dataLoading) {
				return <CircularProgress />;
			}
			return <></>;
		}
		const totalUsage = defineTotalUsage(data);
		return (
			<div style={{ paddingTop: '40px' }}>
				<Caption color="disabled-light" disableMargin>
					{translate('numbers.usage.dataBreakdown')}
				</Caption>
				{dataUsages.map((dataUsage) => {
					const usageInCorrectUnit = data.totalBalance.unit === 'MB' ? dataUsage.usageMb : dataUsage.usageGB;
					const percentageUsage = (usageInCorrectUnit / totalUsage) * 100;
					return (
						<div key={dataUsage.name} className={classes.dataUsageContainer}>
							<div>
								<ConditionalRender
									show={getCorrectIcon(dataUsage.name) !== ''}
									onTrue={() => {
										return (
											<img
												style={{ width: '32px', height: '32px' }}
												src={getCorrectIcon(dataUsage.name)}
											/>
										);
									}}
									onFalse={() => {
										return (
											<div
												style={{
													width: '32px',
													height: '32px',
													borderRadius: '3px',
													backgroundColor: getBackgroundColor(dataUsage.name),
													display: 'flex',
													justifyContent: 'center',
													alignItems: 'center',
												}}
											>
												{dataUsage.name
													.split(/[ _]+/)
													.map((w) => w.charAt(0))
													.join('')
													.substring(0, 2)}
											</div>
										);
									}}
								/>
							</div>
							<div className={classes.linearProgressContainer}>
								<span>{dataUsage.name}</span>
								<div className={classes.leftBundleNumber}></div>
								<div className={classes.linearProgress}>
									<div style={{ width: percentageUsage + '%' }}>
										<CustomLinearProgress
											variant="determinate"
											color={'primary'}
											value={0}
											className={classes.balanceProgress}
										/>
									</div>
									<div className={classes.leftBundle}>
										<BodyS inline color="disabled-light">{`${usageInCorrectUnit}`}</BodyS>
										<BodyS inline color="disabled-light" className={classes.unit}>
											{data.totalBalance.unit}
										</BodyS>
									</div>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	const generalPackages = ((bundles) => {
		const total = bundles.totals.find(({ group }) => group === 'GENERAL');
		const packages = bundles.packages.filter((singlePackage) => {
			const group = singlePackage.group;
			const plan = singlePackage.plan;
			const productType = singlePackage.commerceAttributes?.productType;
			return group === 'GENERAL' && productType !== 'INT_MIN' && plan === 'NAMA';
		});

		if (!total) {
			return <NoBundle />;
		}

		const Total = (
			<>
				<BundleLeft
					header={translate(`numbers.usage.${match.params.type}Left`)}
					data={total}
					unlimitedText={translate('numbers.usage.unlimited')}
				/>
				<ConditionalRender
					show={match.params.type === 'data'}
					onTrue={() => {
						return <DataUsages data={total} />;
					}}
				/>
			</>
		);
		return (
			<div>
				<Card className={classes.generalUsageCard}>
					<CardBody>
						<div className={classes.usageDetailsHeader}>
							<Hidden xsDown>
								<div className={classes.iconContainer}>{Icon('general')}</div>
							</Hidden>
							<div className={classes.totalContainer}>{Total}</div>
						</div>
					</CardBody>
				</Card>
				<GridContainer justify="flex-start">
					{packages.map((bundle, index) => {
						return (
							<GridItem className={classes.bundleSubpackage} key={index} xs={12} md={6} lg={4}>
								<Card className={classes.bundleSubpackageCard}>
									<CardBody>
										<SinglePackage singlePackage={bundle} />
									</CardBody>
								</Card>
							</GridItem>
						);
					})}
				</GridContainer>
			</div>
		);
	})(bundles);

	const specialPackages = ((bundles) => {
		const packages = bundles.packages.filter(
			({ group, plan }) => group === 'SPECIAL' || (group === 'GENERAL' && plan !== 'NAMA')
		);
		return renderPackages(packages, Icon('special'));
	})(bundles);

	const roamingPackages = ((bundles) => {
		const packages = bundles.packages.filter(({ group }) => group === 'ROAMING');
		return renderPackages(packages, Icon('roaming'));
	})(bundles);

	const internationalPackages = ((bundles) => {
		const packages = bundles.packages.filter((singlePackage) => {
			const productType = singlePackage.commerceAttributes?.productType;
			return productType === 'INT_MIN';
		});
		return renderPackages(packages, Icon('international'));
	})(bundles);

	return (
		<GridContainer>
			<BreadcrumbsItem to={match.url}>{translate(`path.${match.params.type}.usage.details`)}</BreadcrumbsItem>
			<GridItem xs={12}>
				<HeadingLBold color="secondary-light">
					{translate(`numbers.usage.details.${match.params.type}.header`)}
				</HeadingLBold>
			</GridItem>
			<GridItem xs={12} className={classes.bundlesCategory}>
				{generalPackages}
			</GridItem>
			<GridItem xs={12}>
				<HeadingLBold color="secondary-light">{translate('numbers.usage.special')}</HeadingLBold>
			</GridItem>
			<GridItem xs={12}>{specialPackages}</GridItem>
			<GridItem xs={12}>
				<HeadingLBold color="secondary-light">{translate('numbers.usage.roaming')}</HeadingLBold>
			</GridItem>
			<GridItem xs={12}>{roamingPackages}</GridItem>
			<ConditionalRender
				show={type === 'voice'}
				onTrue={() => (
					<>
						<GridItem xs={12}>
							<HeadingLBold color="secondary-light">
								{translate('numbers.usage.international')}
							</HeadingLBold>
						</GridItem>
						<GridItem xs={12}>{internationalPackages}</GridItem>
					</>
				)}
			/>
		</GridContainer>
	);
};

export default SingleUsageDetails;
